import { useStore } from "@nanostores/vue";
import { createInjectionState } from "@vueuse/shared";
import { computed, ref } from "vue";
import { TRACK_SHOPPING } from "~/configs/track";
import httpResources from "~/libs/axios";
import shopifyCart from "~/services/shopify-cart";
import manualCart from "~/services/manual-cart";
import { ECOMMERCE_SITE } from "@/configs/settings";
import {
	$cart,
	$modal,
	$productDetail,
	$productExternalDetail,
	addOrUpdateCart,
	deleteCart,
	toggleModal,
} from "~/store/cart";

import { SHORT_VIDEO_EVENT_NAMES } from "~/core/short-video-events";
import { $playlistData } from "~/store/playlist";

const httpInstance = httpResources;

const [useProvideCartIntegration, _useCartIntegration] = createInjectionState(
	(props) => {
		const cartInstance =
			props.ecommerceSite === ECOMMERCE_SITE.SHOPIFY ? shopifyCart : manualCart;
		const videoId = computed(() => props.videoId);
		const playlistId = computed(() => props.playlistId);
		const products = computed(() => props.products);
		const productDetail = useStore($productDetail);
		const productExternalDetail = useStore($productExternalDetail);
		const playlistData = useStore($playlistData);
		const viewBtnText = computed(() => props.viewBtnText);
		const cart = useStore($cart);
		const modal = useStore($modal);
		const loading = ref(false);

		const playlistDetail = computed(() =>
			playlistData.value ? playlistData.value[playlistId.value] : null
		);

		const videoDetail = computed(() =>
			playlistDetail.value?.videos?.find((video) => video.id === videoId.value)
		);

		const videoTracking = computed(
			() => modal.value[videoId.value].videoTracking
		);

		const openProductList = computed({
			get() {
				return modal.value[videoId.value].openProductList;
			},
			set(val) {
				toggleModal(videoId.value, { openProductList: val });
			},
		});

		const openProductDetail = computed({
			get() {
				return modal.value[videoId.value].openProductDetail;
			},
			set(val) {
				toggleModal(videoId.value, { openProductDetail: val });
			},
		});

		const openProductExternalDetail = computed({
			get() {
				return modal.value[videoId.value].openProductExternalDetail;
			},
			set(val) {
				toggleModal(videoId.value, { openProductExternalDetail: val });
			},
		});

		const openCart = computed({
			get() {
				return modal.value[videoId.value].openCart;
			},
			set(val) {
				toggleModal(videoId.value, { openCart: val });
				const cartProducts = formatTrackingItem(cart.value);
				if (val) {
					// OPEN CART
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(SHORT_VIDEO_EVENT_NAMES.OPEN_CART, {
						items: cartProducts,
					});
				} else {
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(SHORT_VIDEO_EVENT_NAMES.CLOSE_CART, {
						items: cartProducts,
					});
				}
			},
		});

		function getCartEventManager() {
			if (
				cartInstance.infoInternal?.[`${videoId.value}_${playlistId.value}`]
					.eventManager
			) {
				return cartInstance.infoInternal?.[
					`${videoId.value}_${playlistId.value}`
				].eventManager;
			}
			return cartInstance.eventManager;
		}

		function formatTrackingItem(items, data = {}) {
			return items.map((item) => {
				return {
					id: item.id,
					name: item?.name,
					item_id: `${item.id}`,
					item_name: item?.name,
					image: item?.image,
					price: item?.price,
					quantity: item?.quantity,
					lora_id: item.lora_id,
					sku: item?.sku,
					...data,
				};
			});
		}

		async function addToCart(variant, quantity) {
			const { id, name, images, price, promotionPrice } = variant;
			const existCartItem = cart.value.find((item) => item.id === id);
			let newQuantity = quantity;
			if (existCartItem) newQuantity += existCartItem.quantity;

			let isSuccess = false;
			let error = undefined;
			let data = undefined;
			loading.value = true;
			try {
				const cmsProduct = getProductByVariant(id);
				const properties = {
					...productDetail.value.properties,
					productId: cmsProduct?.id,
					sku: cmsProduct?.productSku,
					videoId: videoId.value,
					organizationId: props.organizationId,
					playlistId: props.playlistId || undefined,
				};
				const res = await cartInstance.addToCart(
					id,
					quantity,
					properties,
					videoId.value,
					playlistId.value
				);
				if (res.items || res.success === true) {
					isSuccess = true;
					data = {
						id,
						name,
						image: images[0] || productDetail.value.image,
						price: promotionPrice > 0 ? promotionPrice : price,
						quantity: newQuantity,
						properties,
					};
					addOrUpdateCart(data);
					trackShopping(TRACK_SHOPPING.ADD_TO_CART, { ...data, quantity });
				} else {
					error = res;
				}
			} catch (err) {
				error = err;
			}
			loading.value = false;
			return { isSuccess, error, data };
		}

		async function updateItemInCart(variantId, quantity, isDecrease = false) {
			loading.value = true;
			let isSuccess = false;
			let error = undefined;
			try {
				const cartItem = cart.value.find((item) => item.id === variantId);
				let properties = cartItem.properties || {};
				if (!cartItem.properties?.videoId) {
					const cmsProduct = getProductByVariant(variantId);
					if (cmsProduct) {
						properties = {
							productId: cmsProduct?.id,
							sku: cmsProduct?.productSku,
							videoId: videoId.value,
							organizationId: props.organizationId,
							playlistId: props.playlistId || undefined,
						};
					}
				}
				const existCartItem = { ...cartItem, properties };
				const res = await cartInstance.updateItemInCart(
					{
						id: `${variantId}`,
						quantity,
						properties: {
							...productDetail.value.properties,
							...properties,
						},
					},
					videoId.value,
					playlistId.value
				);
				loading.value = false;
				if (!(res.items || res.success === true)) {
					return { isSuccess, error: res };
				}

				if (quantity > 0) {
					addOrUpdateCart({
						id: variantId,
						quantity,
					});
				} else {
					deleteCart(variantId);
				}
				isSuccess = true;

				if (quantity === 0) {
					trackShopping(TRACK_SHOPPING.REMOVE_FROM_CART, {
						...existCartItem,
						quantity: existCartItem.quantity,
					});
				} else {
					if (isDecrease) {
						trackShopping(TRACK_SHOPPING.DECREASE_FROM_CART, {
							...existCartItem,
							quantity: 1,
						});
					} else {
						trackShopping(TRACK_SHOPPING.ADD_TO_CART, {
							...existCartItem,
							quantity: 1,
						});
					}
				}
			} catch (err) {
				error = err;
			}
			loading.value = false;
			return { isSuccess, error };
		}

		function getProductByVariant(variantId) {
			const cmsProduct = products.value.find((item) =>
				item.variants.some((variant) => variant.id === variantId)
			);
			return cmsProduct;
		}

		async function trackShopping(trackingType, item = {}) {
			const cartId = cartInstance.getCartId();
			const basePayload = {
				clientType: 1,
				trackingType,
				trackedAt: new Date().toISOString(),
				cartId: cartId ? cartId?.toString() : "",
				currency: cartInstance.getCurrency(),
			};

			if (trackingType === TRACK_SHOPPING.CHECKOUT) {
				let validCartItems = cart.value.filter(
					(cartItem) => !!cartItem.properties?.videoId
				);
				if (validCartItems.length === 0) return;

				validCartItems = validCartItems.map((validCartItem) => {
					return {
						...validCartItem,
						lora_id: validCartItem?.properties?.productId,
						sku: validCartItem?.properties?.sku,
					};
				});
				// Tracking Checkout
				trackingGoogleGTM(trackingType, validCartItems, {
					cart_id: basePayload.cartId,
					org_id: props.organizationId,
					video_id: videoId.value,
					video_title: videoDetail.value?.title,
					playlist_id: props.playlistId || undefined,
					playlist_title: playlistDetail.value?.data?.title,
				});

				const dataByVideoId = {};
				validCartItems.forEach((cartItem) => {
					if (!cartItem.properties.videoId) return;
					if (!dataByVideoId[cartItem.properties.videoId]) {
						dataByVideoId[cartItem.properties.videoId] = {
							...basePayload,
							shortVideoId: cartItem.properties.videoId,
							playlistId: cartItem.properties.playlistId,
							organizationId: cartItem.properties.organizationId,
							products: [],
						};
					}
					dataByVideoId[cartItem.properties.videoId].products.push({
						variantId: `${cartItem.id}`,
						productName: cartItem.name,
						imageUrl: cartItem.image,
						price: cartItem.price,
						quantity: cartItem.quantity,
						sku: cartItem.properties.sku,
						productId: cartItem.properties.productId,
					});
				});
				const listPromise = [];
				for (let prop in dataByVideoId) {
					const data = dataByVideoId[prop];
					listPromise.push(
						httpInstance.repositories.shortVideo.trackShopping(data)
					);
				}
				return Promise.all(listPromise);
			}

			if (!item.properties?.videoId) {
				return;
			}

			trackingGoogleGTM(
				trackingType,
				[
					{
						...item,
						lora_id: item?.properties?.productId,
						sku: item?.properties?.sku,
					},
				],
				{
					cart_id: basePayload.cartId,
					org_id: props.organizationId,
					video_id: videoId.value,
					video_title: videoDetail.value?.title,
					playlist_id: props.playlistId || undefined,
					playlist_title: playlistDetail.value?.data?.title,
				}
			);

			return httpInstance.repositories.shortVideo.trackShopping({
				...basePayload,
				shortVideoId: videoId.value,
				organizationId: props.organizationId,
				playlistId: props.playlistId || undefined,
				//userDisplayName: "string",
				products: [
					{
						variantId: `${item.id}`,
						productName: item.name,
						imageUrl: item.image,
						price: item.price,
						quantity: item.quantity,
						sku: item.properties?.sku,
						productId: item.properties?.productId,
					},
				],
			});
		}

		async function goCheckout() {
			loading.value = true;
			await cartInstance.goCheckout(videoId.value, playlistId.value);
			loading.value = false;
		}

		function trackingGoogleGTM(type, items, data) {
			if (typeof window.LoraStreamGoogleAnalytics !== "undefined") {
				const itemsProducts = formatTrackingItem(items, data);
				if (type === TRACK_SHOPPING.CHECKOUT) {
					// Calculate total price using reduce
					const totalPrice = items.reduce((accumulator, currentItem) => {
						return accumulator + currentItem.price;
					}, 0);
					window.LoraStreamGoogleAnalytics.trackingCheckout(
						itemsProducts,
						totalPrice,
						cartInstance.getCurrency(),
						"",
						data
					);
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(SHORT_VIDEO_EVENT_NAMES.CHECKOUT, {
						...data,
						items: itemsProducts,
						total_price: totalPrice,
					});
				} else if (type === TRACK_SHOPPING.ADD_TO_CART) {
					const value = itemsProducts.reduce(
						(acc, item) => acc + item.price * item.quantity,
						0
					);
					window.LoraStreamGoogleAnalytics.trackingAddToCart(
						itemsProducts,
						value,
						cartInstance.getCurrency(),
						data
					);
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(SHORT_VIDEO_EVENT_NAMES.ADD_TO_CART, {
						...data,
						items: itemsProducts,
					});
				} else if (type === TRACK_SHOPPING.DECREASE_FROM_CART) {
					const value = itemsProducts.reduce(
						(acc, item) => acc + item.price * item.quantity,
						0
					);
					window.LoraStreamGoogleAnalytics.trackingDecreaseFromCart(
						itemsProducts,
						value,
						cartInstance.getCurrency(),
						data
					);
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(
						SHORT_VIDEO_EVENT_NAMES.DECREASE_FROM_CART,
						{
							...data,
							items: itemsProducts,
						}
					);
				} else if (type === TRACK_SHOPPING.REMOVE_FROM_CART) {
					const value = itemsProducts.reduce(
						(acc, item) => acc + item.price * item.quantity,
						0
					);
					window.LoraStreamGoogleAnalytics.trackingRemoveFromCart(
						itemsProducts,
						value,
						cartInstance.getCurrency(),
						data
					);
					const currentCartEventManager = getCartEventManager();
					currentCartEventManager.emit(
						SHORT_VIDEO_EVENT_NAMES.REMOVE_FROM_CART,
						{
							...data,
							currency: cartInstance.getCurrency(),
							items: itemsProducts,
						}
					);
				}
			}
		}

		return {
			trackShopping,
			trackingGoogleGTM,
			cartInstance,
			videoId,
			products,
			loading,
			viewBtnText,
			productDetail,
			productExternalDetail,
			openProductList,
			openProductDetail,
			openProductExternalDetail,
			openCart,
			cart,
			setCart: $cart.set,
			addToCart,
			updateItemInCart,
			goCheckout,
			addOrUpdateCart,
			deleteCart,
			videoTracking,
			currency: cartInstance.getCurrency(),
		};
	}
);

export { useProvideCartIntegration };

export function useCartIntegration() {
	const store = _useCartIntegration();
	if (store == null)
		throw new Error(
			"Please call `useProvideCartIntegration` on the appropriate parent component"
		);
	return store;
}
